import { memo, useCallback, useEffect, useState } from "react";
import { Pagination } from "services/search";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { NoResults } from "ui/components/NoResults";
import { TableSearch } from "ui/components/Table/TableSearch";
import { DASHBOARD_OFFER_SEARCH_COLUMNS } from "services/api/dashboard/consts";
import { transformRoute, useTranslate } from "helpers";
import {
  deleteFleetVehicleOfferSaleAPI,
  getFleetVehicleOfferSaleAPI,
  getFleetVehicleOfferSalesAPI,
} from "services/api/fleet/vehicles/offers/api";
import { FleetVehicleOfferSaleRow } from "services/api/fleet/vehicles/offers/types";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import { useNavigate } from "react-router-dom";
import { Dialog } from "ui/components/Dialog";

const OfferSaleSearchTab: React.FC = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleOfferSaleRow>
  >(initialDataWithPagination);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [offerId, setOfferId] = useState<string>("");
  const [offerVehicleId, setOfferVehicleId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleOfferSalesAPI(pagination);
      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    const offer = await getFleetVehicleOfferSaleAPI(id);
    if (offer && offer.vehicle) {
      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleOfferSearchPageRouter, {
          id: offer.vehicle.id,
        })
      );
    }
  }, [navigate]);

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    id: string,
    vehicleId: string
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setOfferId(id);
    setOfferVehicleId(vehicleId);
    setOpenDialog(true);
  };

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!offerId || !offerVehicleId) return;
    try {
      const deleted = await deleteFleetVehicleOfferSaleAPI(offerVehicleId, offerId, t);
      if (deleted) {
        await fetchSearchResult(searchResults.pagination);
      }
      setOpenDialog(false);
      setOfferId("");
      setOfferVehicleId("");
    } catch (err) {
      console.error("Error deleting sale offer:", err);
    }
  }, [offerId, offerVehicleId, t, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setOfferId("");
    setOfferVehicleId("");
    setOpenDialog(false);
  }, []);

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const handleContactClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, contactId: string) => {
      event.preventDefault();
      event.stopPropagation();
      navigate(transformRoute(ContactRoutes.ContactDetailPageRouter, { id: contactId }));
    },
    [navigate]
  );

  return (
    <div className="rounded-md mb-4 h-full w-full">
      <div>
        {!isLoading && searchResults.pagination.totalPages === 0 ? (
          <NoResults message={t("admin.dashboard.widget.offers.search.noresults")} />
        ) : (
          <div className="mx-[-1px] mb-[-1px]">
            <TableSearch
              columns={DASHBOARD_OFFER_SEARCH_COLUMNS(t, handleContactClick, handleDeleteClick)}
              data={searchResults.data}
              selectedRows={[]}
              pagination={
                searchResults.pagination || initialDataWithPagination.pagination
              }
              selectableRows={false}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
      <Dialog
        open={openDialog}
        title={t("admin.fleet.vehicle.offer.dialog.delete.title")}
        message={t("admin.fleet.vehicle.offer.dialog.delete.message")}
        confirmText={t("admin.fleet.vehicle.offer.dialog.delete.confirmtext")}
        cancelText={t("admin.fleet.vehicle.offer.dialog.delete.canceltext")}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </div>
  );
};

export default memo(OfferSaleSearchTab);
